import { create } from 'zustand'
import { webAuth } from '@/lib/web-api'

/**
 * How far through web sign-in this browser is.
 *
 * 'anonymous' — no session; show the sign-in form.
 * 'newStore'  — the visitor is setting up a brand new store.
 * 'choose'    — signed in, but the account has more than one store to pick from.
 * 'pin'       — store chosen and a seat held; the normal PIN screen takes over.
 * 'active'    — PIN accepted; the app runs as it does anywhere else.
 *
 * The authority is always the server-side session record; this is a mirror so the
 * UI can react. Only 'anonymous', 'newStore' and 'choose' need bespoke screens — from
 * 'pin' onwards the existing gate chain (auth:session → RequireAuth → Login) does the
 * work unchanged.
 *
 * 'newStore' is the one stage that is purely client-side: no session exists until the
 * store does (web_sessions.store_id references stores), so the server has nothing to
 * remember. A reload during sign-up therefore lands back on 'anonymous', which is
 * correct — nothing was created.
 */
export type WebStage = 'unknown' | 'anonymous' | 'newStore' | 'choose' | 'pin' | 'active'

export interface StoreChoice {
  id: string
  name: string
}

interface WebStageState {
  stage: WebStage
  stores: StoreChoice[]
  storeName: string | null
  /** Activation code handed over in the URL by the marketing site. See takeCodeFromUrl. */
  signupCode: string | null
  /**
   * True when the visitor arrived on `?trial=1` — the marketing site's "start free
   * trial" button. Switches the setup screen from asking for an activation code to
   * asking for a phone number. Purely which form to show; the server decides whether
   * a trial is actually allowed.
   */
  trialSignup: boolean
  hydrate: () => Promise<void>
  set: (stage: WebStage, extra?: { stores?: StoreChoice[]; storeName?: string | null }) => void
  /** Switches the setup screen between the paid and trial forms. */
  setTrialSignup: (trial: boolean) => void
}

/**
 * Reads `?code=` once, and removes it from the URL.
 *
 * The marketing site puts the freshly-bought activation code here so a new customer
 * lands on store setup with it already filled in, instead of a sign-in form for an
 * account they do not have yet.
 *
 * Stripped immediately, for three reasons: the code should not sit in browser history
 * or a bookmark; nothing should send it as a Referer (same-origin only under our CSP,
 * but still); and a reload after the store exists must not re-offer a code that has
 * already been spent.
 *
 * Shape-filtered rather than trusted — it lands in a text input, and the real
 * validation is the server's. Anything else is simply ignored, which degrades to
 * today's behaviour (an empty form).
 */
function takeCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get('code')
    if (!raw) return null
    url.searchParams.delete('code')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    const code = raw.trim().toUpperCase()
    return /^[A-Z0-9-]{4,32}$/.test(code) ? code : null
  } catch {
    return null
  }
}

/**
 * Reads `?trial=1` once, and removes it from the URL.
 *
 * Same one-shot handling as takeCodeFromUrl, for the same reasons: a reload after the
 * store exists must not drop the visitor back onto a signup form, and the parameter
 * has no business surviving in history. It carries no authority — it only chooses
 * which form is rendered.
 */
function takeTrialFromUrl(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get('trial')
    if (!raw) return false
    url.searchParams.delete('trial')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    return raw === '1' || raw.toLowerCase() === 'true'
  } catch {
    return false
  }
}

export const useWebStage = create<WebStageState>((set) => ({
  stage: 'unknown',
  stores: [],
  storeName: null,
  signupCode: null,
  trialSignup: false,

  async hydrate() {
    // Before the round trip, so the params are gone from the URL either way.
    const signupCode = takeCodeFromUrl()
    const trialSignup = takeTrialFromUrl()

    const res = await webAuth<{
      stage: WebStage
      storeName: string | null
      stores?: StoreChoice[]
    }>('session')

    // A code in the URL means "I have just bought this and have no store yet", so it
    // only ever applies to a browser with no session. Someone already signed in keeps
    // their session — they can renew from Settings, where the store is unambiguous.
    // `?trial=1` is the same bargain: an existing session is not thrown away to offer
    // a trial to someone who already has a store.
    if ((signupCode || trialSignup) && (!res.ok || res.data.stage === 'anonymous')) {
      // A code wins if somehow both are present: it is the paid intent, and it is the
      // one that would be wasted by guessing wrong.
      set({ stage: 'newStore', signupCode, trialSignup: trialSignup && !signupCode })
      return
    }

    if (res.ok) {
      // The list matters on reload too: refreshing at the store picker would
      // otherwise render an empty one with nothing to click.
      set({
        stage: res.data.stage,
        storeName: res.data.storeName,
        stores: res.data.stores ?? []
      })
    } else {
      set({ stage: 'anonymous' })
    }
  },

  set(stage, extra) {
    set({
      stage,
      ...(extra?.stores ? { stores: extra.stores } : {}),
      ...(extra?.storeName !== undefined ? { storeName: extra.storeName } : {})
    })
  },

  setTrialSignup(trial) {
    set({ trialSignup: trial })
  }
}))

/**
 * Called from the transport when a request comes back SESSION_EXPIRED, so a session
 * that dies underneath the app (idle, signed out elsewhere, or evicted by the same
 * login on another machine) drops straight back to sign-in instead of every screen
 * inventing its own error.
 */
export function setWebStage(stage: WebStage): void {
  useWebStage.getState().set(stage)
}
